import { ReadPersonDto } from '@/api.types';
import { describeFormValidationError } from '@/shared/utils/formZod.utils';
import { toUsuarioVisaoFormValue } from '@/shared/constants/visao-sistema.constants';

export type OperationalProfileKind = 'defensor' | 'estagiario' | 'colaborador';

export type AutorizacaoTrabalhoRemotoFormDto = {
  id?: number;
  data_inicial?: string;
  data_final?: string | null;
  anotacao?: string | null;
  uiId?: string;
};

export type VinculoAssessorDefensorFormDto = {
  id?: number;
  colaborador_id?: number;
  colaborador_nome?: string | null;
  data_inicial?: string;
  data_final?: string | null;
  uiId?: string;
};

export type VinculoDefensorAssessorFormDto = {
  id?: number;
  defensor_id?: number;
  defensor_nome?: string | null;
  data_inicial?: string;
  data_final?: string | null;
  uiId?: string;
};

type PersonFormOperationalProfile<T> = Partial<T> & { ativo?: boolean };

/** Listas editáveis no formulário aceitam itens novos (sem `id`), então substituem os tipos da API. */
export type PersonFormDefensor = PersonFormOperationalProfile<
  Omit<NonNullable<ReadPersonDto['defensor']>, 'autorizacoesTrabalhoRemoto' | 'vinculosAssessores'>
> & {
  /** Campo do formulário (ainda não espelhado no DTO gerado da API). */
  categoria?: string | null;
  autorizacoesTrabalhoRemoto?: AutorizacaoTrabalhoRemotoFormDto[];
  vinculosAssessores?: VinculoAssessorDefensorFormDto[];
};

export type PersonFormColaborador = PersonFormOperationalProfile<
  Omit<NonNullable<ReadPersonDto['colaborador']>, 'vinculosDefensores'>
> & {
  vinculosDefensores?: VinculoDefensorAssessorFormDto[];
};

export type OperationalProfileData =
  | PersonFormDefensor
  | PersonFormOperationalProfile<NonNullable<ReadPersonDto['estagiario']>>
  | PersonFormColaborador;

export type PersonFormUsuario = Omit<NonNullable<ReadPersonDto['usuario']>, 'visao'> & {
  visao?: string | number | null;
};

export type OperationalProfileEntry = {
  localKey: string;
  kind: OperationalProfileKind;
  id?: number;
  ativo: boolean;
  isDraft: boolean;
  dirty?: boolean;
  data: OperationalProfileData;
};

export type OperationalProfileRemoval = {
  kind: OperationalProfileKind;
  id: number;
};

export type PersonFormDto = Omit<
  ReadPersonDto,
  | 'usuario'
  | 'defensor'
  | 'estagiario'
  | 'colaborador'
  | 'defensoresHistorico'
  | 'estagiariosHistorico'
  | 'colaboradoresHistorico'
> & {
  defensor?: PersonFormDefensor;
  estagiario?: PersonFormOperationalProfile<NonNullable<ReadPersonDto['estagiario']>>;
  colaborador?: PersonFormColaborador;
  usuario?: PersonFormUsuario;
  defensoresHistorico?: PersonFormDefensor[];
  estagiariosHistorico?: PersonFormOperationalProfile<NonNullable<ReadPersonDto['estagiario']>>[];
  colaboradoresHistorico?: PersonFormColaborador[];
  perfisOperacionais?: OperationalProfileEntry[];
  perfisOperacionaisRemovidos?: OperationalProfileRemoval[];
};

/** Referência mínima de perfil (usuário, colaborador, etc.) no formulário. */
export type ProfileRef = { id?: number; ativo?: boolean };

/**
 * Perfil presente no formulário: cadastro existente (`id`) ou marcado ativo.
 * Mesma regra de `edit.tsx` ao decidir se o perfil vai no payload (`!id && !ativo` → remove).
 */
export function profileHasPresence(profile?: ProfileRef): boolean {
  return !!(profile?.id || profile?.ativo);
}

export function shouldIncludeProfileInPayload(
  profile: ProfileRef | undefined,
  podeVer: boolean,
  tipoMatches: boolean,
): boolean {
  return podeVer && tipoMatches && profileHasPresence(profile);
}

/** Converte `null`/`undefined` em string vazia (API e inputs desabilitados). */
function str(value: unknown): string {
  return value == null ? '' : String(value);
}

function bool(value: unknown): boolean | undefined {
  if (value === null || value === undefined) return undefined;
  return value === true || value === 'true';
}

/** Perfis ausentes na API vêm como `null`; Zod `.optional()` só aceita `undefined`. */
function profileOrUndefined<T>(value: T | null | undefined): T | undefined {
  return value == null ? undefined : value;
}

/** Relação opcional (`funcao`) também chega como `null` quando não há vínculo. */
function optionalRelation<T>(value: T | null | undefined): T | undefined {
  return value == null ? undefined : value;
}

/** Normaliza valores da API para o formulário (Zod não aceita `null` em `z.string()` / `z.boolean()`). */
export function normalizePersonFormFromApi(pessoa: PersonFormDto): PersonFormDto {
  return {
    ...pessoa,
    email: str(pessoa.email),
    emailInstitucional: str(pessoa.emailInstitucional),
    dataNascimento: str(pessoa.dataNascimento),
    podeserresponsavel: bool(pessoa.podeserresponsavel) ?? false,
    endereco: pessoa.endereco
      ? {
        ...pessoa.endereco,
        cep: str(pessoa.endereco.cep),
        logradouro: str(pessoa.endereco.logradouro),
        bairro: str(pessoa.endereco.bairro),
        cidade: str(pessoa.endereco.cidade),
        estado: str(pessoa.endereco.estado),
        complemento: str(pessoa.endereco.complemento),
        numero: pessoa.endereco.numero ?? undefined,
      }
      : pessoa.endereco,
    telefone: pessoa.telefone
      ? { ...pessoa.telefone, numero: str(pessoa.telefone.numero) }
      : pessoa.telefone,
    celular: pessoa.celular
      ? { ...pessoa.celular, numero: str(pessoa.celular.numero) }
      : pessoa.celular,
    usuario: profileOrUndefined(
      pessoa.usuario
        ? {
          ...pessoa.usuario,
          corfundotabela: str(pessoa.usuario.corfundotabela) || '#e5e5e5',
          corletratabela: str(pessoa.usuario.corletratabela) || '#000000',
          visao: toUsuarioVisaoFormValue(pessoa.usuario.visao),
          loginSenhaHabilitado: bool(pessoa.usuario.loginSenhaHabilitado) ?? false,
          loginTokenHabilitado: bool(pessoa.usuario.loginTokenHabilitado) ?? false,
        }
        : null,
    ),
    colaborador: profileOrUndefined(
      pessoa.colaborador
        ? {
          ...pessoa.colaborador,
          orgao_origem: str(pessoa.colaborador.orgao_origem),
          matricula: str(pessoa.colaborador.matricula),
          anotacao: str(pessoa.colaborador.anotacao),
          data_cessao: str(pessoa.colaborador.data_cessao),
          data_saida: str(pessoa.colaborador.data_saida),
          vinculo_direto: bool(pessoa.colaborador.vinculo_direto) ?? false,
          cessao_onerosa: bool(pessoa.colaborador.cessao_onerosa) ?? false,
          funcao: optionalRelation(pessoa.colaborador.funcao),
          vinculosDefensores: (
            (pessoa.colaborador as PersonFormColaborador).vinculosDefensores ?? []
          ).map((item) => ({
            ...item,
            defensor_id: item.defensor_id == null ? undefined : Number(item.defensor_id),
            defensor_nome: item.defensor_nome == null ? null : String(item.defensor_nome),
            data_final: item.data_final == null || item.data_final === '' ? null : item.data_final,
          })),
        }
        : null,
    ),
    defensor: profileOrUndefined(
      pessoa.defensor
        ? ({
          ...pessoa.defensor,
          funcao: optionalRelation(pessoa.defensor.funcao),
          categoria: str(pessoa.defensor.categoria),
          // Datas das autorizações: só em `carregarPessoa` via formatAutorizacoes/DataIso.
          autorizacoesTrabalhoRemoto: (
            (pessoa.defensor as PersonFormDefensor).autorizacoesTrabalhoRemoto ?? []
          ).map((item) => ({
            ...item,
            data_final: item.data_final == null || item.data_final === '' ? null : item.data_final,
            anotacao: item.anotacao == null ? null : String(item.anotacao),
          })),
          vinculosAssessores: (
            (pessoa.defensor as PersonFormDefensor).vinculosAssessores ?? []
          ).map((item) => ({
            ...item,
            colaborador_id: item.colaborador_id == null ? undefined : Number(item.colaborador_id),
            colaborador_nome: item.colaborador_nome == null ? null : String(item.colaborador_nome),
            data_final: item.data_final == null || item.data_final === '' ? null : item.data_final,
          })),
        } as PersonFormDefensor)
        : null,
    ),
    estagiario: profileOrUndefined(
      pessoa.estagiario
        ? {
          ...pessoa.estagiario,
          anotacao: str(pessoa.estagiario.anotacao),
          estagios: pessoa.estagiario.estagios?.map((estagio) => ({
            ...estagio,
            fim_semana: bool(estagio.fim_semana) ?? false,
          })),
        }
        : null,
    ),
    defensoresHistorico: (pessoa.defensoresHistorico ?? []).map(
      (item) =>
        ({
          ...item,
          categoria: item?.categoria == null ? null : str(item.categoria),
          // Mesmo contrato do defensor ativo: null em data_final; datas formatadas em edit.tsx.
          autorizacoesTrabalhoRemoto: (item?.autorizacoesTrabalhoRemoto ?? []).map((auth) => ({
            ...auth,
            data_final: auth.data_final == null || auth.data_final === '' ? null : auth.data_final,
            anotacao: auth.anotacao == null ? null : String(auth.anotacao),
          })),
          vinculosAssessores: (item?.vinculosAssessores ?? []).map((vinculo) => ({
            ...vinculo,
            colaborador_id: vinculo.colaborador_id == null ? undefined : Number(vinculo.colaborador_id),
            colaborador_nome: vinculo.colaborador_nome == null ? null : String(vinculo.colaborador_nome),
            data_final: vinculo.data_final == null || vinculo.data_final === '' ? null : vinculo.data_final,
          })),
        }) as PersonFormDefensor,
    ),
    estagiariosHistorico: (pessoa.estagiariosHistorico ?? []).map((item) => ({
      ...item,
      anotacao: str(item?.anotacao),
      estagios: item?.estagios?.map((estagio) => ({
        ...estagio,
        fim_semana: bool(estagio.fim_semana) ?? false,
      })),
    })) as PersonFormDto['estagiariosHistorico'],
    colaboradoresHistorico: (pessoa.colaboradoresHistorico ?? []).map((item) => ({
      ...item,
      orgao_origem: str(item?.orgao_origem),
      anotacao: str(item?.anotacao),
      data_cessao: str(item?.data_cessao),
      data_saida: str(item?.data_saida),
      vinculosDefensores: (item?.vinculosDefensores ?? []).map((vinculo) => ({
        ...vinculo,
        defensor_id: vinculo.defensor_id == null ? undefined : Number(vinculo.defensor_id),
        defensor_nome: vinculo.defensor_nome == null ? null : String(vinculo.defensor_nome),
        data_final: vinculo.data_final == null || vinculo.data_final === '' ? null : vinculo.data_final,
      })),
    })) as PersonFormDto['colaboradoresHistorico'],
  } as PersonFormDto;
}

const PERSON_FORM_FIELD_LABELS: Record<string, string> = {
  nome: 'Nome',
  cpf: 'CPF',
  email: 'E-mail',
  emailInstitucional: 'E-mail institucional',
  'defensor.funcao': 'Função do defensor',
  'colaborador.funcao': 'Função do colaborador',
  'colaborador.tipo_vinculo': 'Tipo de vínculo',
  'usuario.senha': 'Senha',
  'usuario.loginSenhaHabilitado': 'Forma de login',
};

/** Toast quando a validação falha: aponta o campo, mesmo se ele não estiver visível. */
export function describePersonFormValidationError(errors: unknown): string {
  return describeFormValidationError(errors, PERSON_FORM_FIELD_LABELS);
}
